import React, { useEffect, useState } from "react";
import {
  Table, Tag, Button, Input, Modal, message, Dropdown, Space,
  Tooltip, Form, Select, Row, Col, Drawer, Avatar, Divider, Upload, Pagination,
} from "antd";
import {
  SearchOutlined, EyeOutlined, DeleteOutlined, UserOutlined,
  PhoneOutlined, MailOutlined, EditOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownOutlined, UpOutlined, UserAddOutlined,
  WalletOutlined, CalendarOutlined, EnvironmentOutlined,
  TeamOutlined, CheckCircleOutlined, ClockCircleOutlined,
  DollarOutlined, TransactionOutlined, IdcardOutlined,
  CopyOutlined, LinkOutlined, WhatsAppOutlined, LockOutlined,
  CameraOutlined, HeartFilled, SafetyOutlined, CrownOutlined,
  CloseCircleOutlined, StopOutlined, HourglassOutlined,
} from "@ant-design/icons";
import {
  UserGet, UserRemove, UserPost, UserUpdate, StaffTransactionsGet, staffApproveReject, User_StateGet,
  getDistrictsByState,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import {
  formatDate, formatCurrency, generateRefNumber, capitalize,
} from "../../Utils/formatters";
import "./Staff_Tables.css";

const { Option } = Select;

// ⭐ REUSABLE DETAIL ITEM COMPONENT
const DetailItem = ({ icon: Icon, label, value, isLast = false, isRef = false }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '30px 100px 1fr',
    alignItems: 'center',
    gap: 16,
    padding: '10px 0',
    borderBottom: isLast ? 'none' : '1px dashed #f0f0f0',
  }}>
    <Icon style={{ color: '#999', fontSize: 16, justifySelf: 'start' }} />
    <strong style={{
      fontSize: 11, color: '#6b7280', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.5px',
      justifySelf: 'start', textAlign: 'left',
    }}>
      {label}
    </strong>
    {isRef ? (
      <span style={{
        display: 'inline-block', padding: '3px 10px',
        background: '#fef3c7', color: '#92400e', borderRadius: 6,
        fontFamily: 'Courier New, monospace', fontSize: 12, fontWeight: 700,
        border: '1px solid #fde68a', justifySelf: 'start', width: 'fit-content',
      }}>
        {value || 'N/A'}
      </span>
    ) : (
      <span style={{
        fontSize: 13, color: '#1e293b', fontWeight: 600,
        wordBreak: 'break-word', lineHeight: 1.4,
        justifySelf: 'start', textAlign: 'left',
      }}>
        {value || 'N/A'}
      </span>
    )}
  </div>
);

function Staff_Tables() {
  const [staff, setStaff] = useState([]);
  const [inactiveStaff, setInactiveStaff] = useState([]);
  const [pendingStaff, setPendingStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [states, setStates] = useState([]);
  const [addDistricts, setAddDistricts] = useState([]);
  const [addDistrictsLoading, setAddDistrictsLoading] = useState(false);
  const [editDistricts, setEditDistricts] = useState([]);
  const [editDistrictsLoading, setEditDistrictsLoading] = useState(false);
  const [activeCurrentPage, setActiveCurrentPage] = useState(1);
  const [activePageSize, setActivePageSize] = useState(10);
  const [inactiveCurrentPage, setInactiveCurrentPage] = useState(1);
  const [inactivePageSize, setInactivePageSize] = useState(5);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(5);

  // 📱 Mobile Responsiveness States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileStats, setShowMobileStats] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const adminName = localStorage.getItem("username") || "Admin";

  const getStaffProfileId = (staffMember) => {
    if (!staffMember) return "N/A";
    if (staffMember.profile_id) return staffMember.profile_id;
    if (staffMember.user?.profile_id) return staffMember.user.profile_id;
    if (staffMember.UserData?.profile_id) return staffMember.UserData.profile_id;
    return `CRST${String(staffMember.id || 1000).padStart(6, "0")}`;
  };

  const getReferralCode = (staffMember) => {
    if (!staffMember) return "";
    const code = (
      staffMember.refer ||
      staffMember.referral_code ||
      staffMember.profile_id ||
      getStaffProfileId(staffMember)
    );
    return code !== null && code !== undefined ? String(code) : "";
  };

  const generateReferralLink = (staffMember) => {
    const baseUrl = window.location.origin;
    const code = getReferralCode(staffMember);
    return `${baseUrl}/User_Reg/${code}`;
  };

  const handleCopyLink = (staffMember) => {
    const link = generateReferralLink(staffMember);
    navigator.clipboard
      .writeText(link)
      .then(() => message.success("✅ Referral link copied!"))
      .catch(() => {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        message.success("✅ Referral link copied!");
      });
  };

  const handleCopyCode = (staffMember) => {
    const code = getReferralCode(staffMember);
    navigator.clipboard
      .writeText(code)
      .then(() => message.success("✅ Referral code copied!"))
      .catch(() => message.error("Copy failed"));
  };

  const handleShareWhatsApp = (staffMember) => {
    const link = generateReferralLink(staffMember);
    const code = getReferralCode(staffMember);
    const name = staffMember.first_name || staffMember.username || "Staff";
    const text = encodeURIComponent(
      `🎉 Join Matrimonial App!\n\nHello! I am ${name}.\nRegister using my referral link:\n\n${link}\n\nReferral Code: ${code}\n\n✅ Register now and find your perfect match!`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const [staffRes, txRes] = await Promise.all([
        UserGet(),
        StaffTransactionsGet(),
      ]);

      const allStaff = Array.isArray(staffRes)
        ? staffRes.filter((i) => i.role === "staff")
        : [];

      const active = allStaff.filter(
        (s) => s.is_active === true && s.is_approved !== false
      );

      const inactive = allStaff.filter(
        (s) => s.is_active === false && s.is_approved === true
      );

      const pending = allStaff.filter(
        (s) =>
          s.is_active === false &&
          (s.is_approved === false ||
            s.is_approved === undefined ||
            s.is_approved === null)
      );

      const txArr = Array.isArray(txRes) ? txRes : [];

      setStaff(active);
      setFilteredStaff(active);
      setInactiveStaff(inactive);
      setPendingStaff(pending);
      setTransactions(txArr);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch staff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const res = await User_StateGet();
        if (Array.isArray(res)) setStates(res);
        else if (res?.data) setStates(res.data);
        else setStates([]);
      } catch {
        setStates([]);
      }
    };
    loadStates();
    fetchStaff();
  }, []);

  useEffect(() => {
    let filtered = [...staff];
    if (searchText) {
      const s = searchText.toLowerCase();
      filtered = filtered.filter(
        (st) =>
          (st.first_name?.toLowerCase() || '').includes(s) ||
          (st.username?.toLowerCase() || '').includes(s) ||
          (st.email?.toLowerCase() || '').includes(s) ||
          (st.contact || '').includes(searchText) ||
          (getStaffProfileId(st) || '').toLowerCase().includes(s) ||
          String(getReferralCode(st) || '').toLowerCase().includes(s)
      );
    }
    setFilteredStaff(filtered);
  }, [searchText, staff]);

  const getStaffTransactions = (staffId) =>
    transactions.filter(
      (t) =>
        String(t.staff_id) === String(staffId) ||
        String(t.user_id) === String(staffId)
    );

  const getStaffBalance = (staffMember) => {
    if (staffMember.balance !== undefined)
      return parseFloat(staffMember.balance || 0);
    const staffTx = getStaffTransactions(staffMember.id);
    return staffTx.reduce((sum, t) => {
      const amt = parseFloat(t.amount) || 0;
      return t.type === "credit" ? sum + amt : sum - amt;
    }, 0);
  };

  const handleCall = (contact) => {
    if (!contact) return message.warning("No contact number");
    window.location.href = `tel:${contact}`;
  };

  const handleViewDetails = (staffMember) => {
    setSelectedStaff(staffMember);
    setDetailsDrawerOpen(true);
  };

  const handleEdit = (staffMember) => {
    setSelectedStaff(staffMember);
    if (staffMember.pic) {
      setPreviewUrl(typeof staffMember.pic === "string" ? staffMember.pic : null);
    }
    editForm.setFieldsValue({
      first_name: staffMember.first_name,
      username: staffMember.username,
      email: staffMember.email,
      contact: staffMember.contact,
      gender: staffMember.gender,
      district: staffMember.district || staffMember.city,
      state: staffMember.state,
    });
    if (staffMember.state) {
      handleEditStateChange(staffMember.state).then(() => {
        editForm.setFieldsValue({
          district: staffMember.district || staffMember.city,
        });
      });
    }
    setEditModalOpen(true);
  };

  const handleAddStateChange = async (value) => {
    form.setFieldsValue({ state: value, district: undefined });
    setAddDistricts([]);
    if (!value) return;
    try {
      setAddDistrictsLoading(true);
      const response = await getDistrictsByState(value);
      const districtList = response?.districts || (Array.isArray(response) ? response : []);
      setAddDistricts(districtList);
    } catch {
      message.error("Failed to load districts");
      setAddDistricts([]);
    } finally {
      setAddDistrictsLoading(false);
    }
  };

  const handleEditStateChange = async (value) => {
    editForm.setFieldsValue({ state: value, district: undefined });
    setEditDistricts([]);
    if (!value) return;
    try {
      setEditDistrictsLoading(true);
      const response = await getDistrictsByState(value);
      const districtList = response?.districts || (Array.isArray(response) ? response : []);
      setEditDistricts(districtList);
    } catch {
      message.error("Failed to load districts");
      setEditDistricts([]);
    } finally {
      setEditDistrictsLoading(false);
    }
  };

  const handleToggleStatus = (staffMember) => {
    const isCurrentlyActive = staffMember.is_active;
    const newStatus = !isCurrentlyActive;
    const action = newStatus ? "Activate" : "Deactivate";
    const actionColor = newStatus ? "#52c41a" : "#faad14";

    Modal.confirm({
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {newStatus ? (
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 22 }} />
          ) : (
            <StopOutlined style={{ color: "#faad14", fontSize: 22 }} />
          )}
          <span>{action} Staff?</span>
        </div>
      ),
      content: (
        <div style={{ padding: "10px 0" }}>
          <p style={{ marginBottom: 12 }}>
            Are you sure you want to <strong>{action.toLowerCase()}</strong> this staff member?
          </p>
          <div style={{
            background: newStatus ? "#f6ffed" : "#fff7e6",
            border: `1px solid ${newStatus ? "#b7eb8f" : "#ffd591"}`,
            padding: 12, borderRadius: 8, marginBottom: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%",
                background: newStatus ? "#52c41a" : "#faad14",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontWeight: 700, fontSize: 16,
              }}>
                {(staffMember.first_name?.[0] || staffMember.username?.[0] || "S").toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, color: "#1e293b" }}>
                  {staffMember.first_name || staffMember.username}
                </div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {getStaffProfileId(staffMember)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      okText: newStatus ? "Activate" : "Deactivate",
      okButtonProps: { style: { background: actionColor, borderColor: actionColor } },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await UserUpdate(staffMember.id, {
            is_active: newStatus,
            status: newStatus ? "active" : "inactive",
          });
          message.success(
            newStatus
              ? `✅ ${staffMember.first_name || staffMember.username} activated successfully!`
              : `⚠️ ${staffMember.first_name || staffMember.username} deactivated.`
          );
          fetchStaff();
        } catch (error) {
          message.error(`Failed to ${action.toLowerCase()} staff`);
        }
      },
    });
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Staff?",
      content: "This staff member will be permanently deleted.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await UserRemove(id);
          message.success("Staff deleted");
          fetchStaff();
        } catch {
          message.error("Failed to delete");
        }
      },
    });
  };

  const handleApproveStaff = (staffMember) => {
    Modal.confirm({
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 22 }} />
          <span>Approve Staff Application?</span>
        </div>
      ),
      content: (
        <div style={{ padding: "10px 0" }}>
          <div style={{ fontWeight: 700 }}>
            {staffMember.first_name || staffMember.username}
          </div>
        </div>
      ),
      okText: "✅ Approve",
      okButtonProps: { style: { background: "#52c41a", borderColor: "#52c41a" } },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await staffApproveReject(staffMember.id, "approve");
          message.success(`✅ ${staffMember.first_name || staffMember.username} approved!`);
          fetchStaff();
        } catch (error) {
          message.error(error.error || "Failed to approve");
        }
      },
    });
  };

  const handleRejectStaff = (staffMember) => {
    let reason = "";
    let shouldDelete = false;

    Modal.confirm({
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CloseCircleOutlined style={{ color: "#ef4444", fontSize: 22 }} />
          <span>Reject Staff Application?</span>
        </div>
      ),
      content: (
        <div style={{ padding: "10px 0" }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 6 }}>
              Rejection Reason (optional):
            </label>
            <Input.TextArea
              rows={2}
              placeholder="Enter reason for rejection..."
              onChange={(e) => { reason = e.target.value; }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <input
              type="checkbox"
              id="deleteUser"
              onChange={(e) => { shouldDelete = e.target.checked; }}
            />
            <label htmlFor="deleteUser" style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>
              🗑️ Also delete this user permanently
            </label>
          </div>
        </div>
      ),
      okText: "❌ Reject",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await staffApproveReject(staffMember.id, "reject", reason, shouldDelete);
          message.success(
            shouldDelete
              ? `🗑️ ${staffMember.first_name || staffMember.username} rejected & deleted.`
              : `❌ ${staffMember.first_name || staffMember.username} rejected.`
          );
          fetchStaff();
        } catch (error) {
          message.error(error.error || "Failed to reject");
        }
      },
    });
  };

  const handleAddStaff = async (values) => {
    try {
      setAddLoading(true);
      await UserPost({ ...values, role: "staff" });
      message.success("✅ Staff added successfully!");
      handleModalClose();
      fetchStaff();
    } catch (error) {
      const errorMsg =
        error?.response?.data?.username?.[0] ||
        error?.response?.data?.email?.[0] ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message || "Failed to add staff";
      message.error(errorMsg);
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdateStaff = async (values) => {
    try {
      setAddLoading(true);
      await UserUpdate(selectedStaff.id, values);
      message.success("✅ Staff updated successfully!");
      handleEditModalClose();
      fetchStaff();
    } catch {
      message.error("Failed to update");
    } finally {
      setAddLoading(false);
    }
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    form.resetFields();
    setFile(null);
    setPreviewUrl(null);
    setAddDistricts([]);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    editForm.resetFields();
    setFile(null);
    setPreviewUrl(null);
    setEditDistricts([]);
  };

  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "profile_id", label: "Profile ID", format: (r) => getStaffProfileId(r) },
    { key: "referral_code", label: "Referral Code", format: (r) => getReferralCode(r) },
    { key: "name", label: "Staff Name", format: (r) => capitalize(r.first_name) || capitalize(r.username) || "N/A" },
    { key: "email", label: "Email", format: (r) => r.email || "N/A" },
    { key: "contact", label: "Mobile", format: (r) => r.contact || "N/A" },
    { key: "balance", label: "Balance", format: (r) => `₹${getStaffBalance(r).toFixed(2)}` },
    { key: "status", label: "Status", format: (r) => (r.is_active ? "Active" : "Inactive") },
    { key: "referral_link", label: "Referral Link", format: (r) => generateReferralLink(r) },
    { key: "created_date", label: "Created", format: (r) => formatDate(r.created_date || r.date_joined) },
  ];

  const handleExportCSV = (all = false) =>
    exportToCSV(all ? [...staff, ...inactiveStaff] : filteredStaff, exportColumns, "staff_report");

  const handleExportPDF = (all = false) =>
    exportToPDF(all ? [...staff, ...inactiveStaff] : filteredStaff, exportColumns, {
      title: "Staff Report",
      subtitle: "Complete list of staff members with referral info",
      filename: "staff_report",
    });

  const totalBalance = [...staff, ...inactiveStaff].reduce((sum, s) => sum + getStaffBalance(s), 0);
  const stats = {
    total: staff.length + inactiveStaff.length,
    active: staff.length,
    inactive: inactiveStaff.length,
    pending: pendingStaff.length,
    totalBalance,
    transactions: transactions.length,
  };

  const activeColumns = [
    {
      title: "#", key: "sno", width: 40, fixed: "left",
      render: (_, __, i) => (
        <span className="sno-cell">
          {(activeCurrentPage - 1) * activePageSize + i + 1}
        </span>
      ),
    },
    {
      title: "Profile ID", key: "profile_id", width: 90,
      render: (_, r) => <span className="ref-cell">{getStaffProfileId(r)}</span>,
    },
    {
      title: "Staff", key: "name", width: 140,
      render: (_, r) => (
        <div className="user-name-cell">
          <div className="user-avatar staff">
            {r.first_name?.[0] || r.username?.[0] || "S"}
          </div>
          <div className="user-name-info">
            <div className="user-name-text">
              {capitalize(r.first_name) || r.username || "N/A"}
            </div>
            <div className="user-email-text">{r.email || "No email"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Mobile / Call", dataIndex: "contact", key: "contact", width: 160,
      render: (c) => (
        <div className="mobile-call-cell">
          <span className="mobile-number">
            <PhoneOutlined /> {c || "N/A"}
          </span>
          {c && (
            <Tooltip title={`Call ${c}`}>
              <Button size="small" icon={<PhoneOutlined />} className="call-quick-btn"
                onClick={(e) => { e.stopPropagation(); handleCall(c); }}>Call</Button>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Referral", key: "referral", width: 110,
      render: (_, r) => {
        const code = getReferralCode(r);
        return (
          <div className="referral-cell">
            <div className="ref-code-row">
              <span className="ref-code-badge">{code}</span>
              <Tooltip title="Copy Code">
                <Button size="small" icon={<CopyOutlined />} className="copy-code-btn"
                  onClick={(e) => { e.stopPropagation(); handleCopyCode(r); }} />
              </Tooltip>
            </div>
            <Space size={2}>
              <Tooltip title={generateReferralLink(r)}>
                <Button size="small" icon={<LinkOutlined />} className="copy-link-btn"
                  onClick={(e) => { e.stopPropagation(); handleCopyLink(r); }}>Link</Button>
              </Tooltip>
              <Tooltip title="Share on WhatsApp">
                <Button size="small" icon={<WhatsAppOutlined />} className="whatsapp-btn"
                  onClick={(e) => { e.stopPropagation(); handleShareWhatsApp(r); }} />
              </Tooltip>
            </Space>
          </div>
        );
      },
    },
    {
      title: "Status", key: "status", width: 75,
      render: () => <Tag className="status-tag active">● Active</Tag>,
    },
    {
      title: "Created", key: "created", width: 85,
      render: (_, r) => (
        <span className="date-cell">{formatDate(r.created_date || r.date_joined)}</span>
      ),
    },
    {
      title: "Actions", key: "action", width: 120, fixed: "right",
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} className="action-btn view"
              onClick={() => handleViewDetails(r)} />
          </Tooltip>
          <Tooltip title="Edit Staff">
            <Button size="small" icon={<EditOutlined />} className="action-btn edit"
              onClick={() => handleEdit(r)} />
          </Tooltip>
          <Tooltip title="Deactivate (Block Login)">
            <Button
              size="small"
              icon={<StopOutlined />}
              className="action-btn"
              style={{
                background: "#fff7e6",
                color: "#faad14",
                border: "1px solid #ffd591",
              }}
              onClick={() => handleToggleStatus(r)}
            />
          </Tooltip>
          <Tooltip title="Delete Staff">
            <Button size="small" icon={<DeleteOutlined />} className="action-btn delete"
              onClick={() => handleDelete(r.id)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const inactiveColumns = [
    {
      title: "#", key: "sno", width: 45,
      render: (_, __, i) => (
        <span className="sno-cell">
          {(inactiveCurrentPage - 1) * inactivePageSize + i + 1}
        </span>
      ),
    },
    {
      title: "Profile ID", key: "profile_id", width: 100,
      render: (_, r) => <span className="ref-cell">{getStaffProfileId(r)}</span>,
    },
    {
      title: "Staff", key: "name", width: 180,
      render: (_, r) => (
        <div className="user-name-cell">
          <div
            className="user-avatar"
            style={{ background: "linear-gradient(135deg, #faad14, #ffc53d)" }}
          >
            {r.first_name?.[0] || r.username?.[0] || "S"}
          </div>
          <div className="user-name-info">
            <div className="user-name-text">
              {capitalize(r.first_name) || r.username || "N/A"}
            </div>
            <div className="user-email-text">{r.email || "No email"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Mobile", dataIndex: "contact", key: "contact", width: 130,
      render: (c) => (
        <span className="mobile-number">
          <PhoneOutlined style={{ color: "#faad14" }} /> {c || "N/A"}
        </span>
      ),
    },
    {
      title: "Reason", key: "reason", width: 160,
      render: (_, r) => (
        <span style={{ fontSize: 11, color: "#faad14", fontStyle: "italic" }}>
          {r.deactivation_reason || "Deactivated by admin"}
        </span>
      ),
    },
    {
      title: "Status", key: "status", width: 100,
      render: () => (
        <Tag className="status-tag inactive">○ Inactive</Tag>
      ),
    },
    {
      title: "Deactivated On", key: "date", width: 120,
      render: (_, r) => (
        <span className="date-cell">{formatDate(r.updated_date || r.date_joined)}</span>
      ),
    },
    {
      title: "Actions", key: "actions", width: 140, fixed: "right",
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="View Details">
            <div className="icon-action view" onClick={() => handleViewDetails(record)}>
              <EyeOutlined />
            </div>
          </Tooltip>
          <Tooltip title="Reactivate Staff">
            <div className="icon-action activate" onClick={() => handleToggleStatus(record)}>
              <CheckCircleOutlined />
            </div>
          </Tooltip>
          <Tooltip title="Delete Permanently">
            <div className="icon-action delete" onClick={() => handleDelete(record.id)}>
              <DeleteOutlined />
            </div>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const pendingColumns = [
    {
      title: "#", key: "sno", width: 45,
      render: (_, __, i) => (
        <span className="sno-cell">
          {(pendingCurrentPage - 1) * pendingPageSize + i + 1}
        </span>
      ),
    },
    {
      title: "Applicant", key: "name", width: 200,
      render: (_, r) => (
        <div className="user-name-cell">
          <div
            className="user-avatar"
            style={{ background: "linear-gradient(135deg, #f59e0b, #fbbf24)" }}
          >
            {r.first_name?.[0] || r.username?.[0] || "S"}
          </div>
          <div className="user-name-info">
            <div className="user-name-text">
              {capitalize(r.first_name) || r.username || "N/A"}
            </div>
            <div className="user-email-text">{r.email || "No email"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Username", dataIndex: "username", key: "username", width: 120,
      render: (u) => <span style={{ fontWeight: 600, color: "#667eea" }}>{u}</span>,
    },
    {
      title: "Mobile", dataIndex: "contact", key: "contact", width: 130,
      render: (c) => (
        <span>
          <PhoneOutlined style={{ color: "#52c41a", marginRight: 4 }} />
          {c || "N/A"}
        </span>
      ),
    },
    {
      title: "Gender", dataIndex: "gender", key: "gender", width: 90,
      render: (g) => (
        <span>{g === "Male" ? "👨" : g === "Female" ? "👩" : "👤"} {g || "N/A"}</span>
      ),
    },
    {
      title: "Location", key: "location", width: 150,
      render: (_, r) => (
        <span>
          <EnvironmentOutlined style={{ color: "#f59e0b", marginRight: 4 }} />
          {r.district || r.city || "N/A"}
          {r.state ? `, ${r.state}` : ""}
        </span>
      ),
    },
    {
      title: "Applied On", key: "date", width: 110,
      render: (_, r) => <span className="date-cell">{formatDate(r.date_joined)}</span>,
    },
    {
      title: "Actions", key: "actions", width: 140, fixed: "right",
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="View Details">
            <div className="icon-action view" onClick={() => handleViewDetails(record)}>
              <EyeOutlined />
            </div>
          </Tooltip>
          <Tooltip title="Approve Application">
            <div className="icon-action approve" onClick={() => handleApproveStaff(record)}>
              <CheckCircleOutlined />
            </div>
          </Tooltip>
          <Tooltip title="Reject Application">
            <div className="icon-action reject" onClick={() => handleRejectStaff(record)}>
              <CloseCircleOutlined />
            </div>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const csvMenuItems = {
    items: [
      { key: "1", label: `Filtered (${filteredStaff.length})`, onClick: () => handleExportCSV(false) },
      { key: "2", label: `All (${staff.length + inactiveStaff.length})`, onClick: () => handleExportCSV(true) },
    ],
  };

  const pdfMenuItems = {
    items: [
      { key: "1", label: `Filtered (${filteredStaff.length})`, onClick: () => handleExportPDF(false) },
      { key: "2", label: `All (${staff.length + inactiveStaff.length})`, onClick: () => handleExportPDF(true) },
    ],
  };

  return (
    <div className="staff-tables-page">
      {/* HEADER + STATS */}
      <div className="header-stats-wrapper">
        <div className="page-header">
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon"><TeamOutlined /></div>
              <div className="title-content">
                <h2 className="page-title">Staff Management</h2>
                <p className="page-subtitle">
                  {stats.active} Active · {stats.inactive} Inactive · {stats.pending} Pending
                </p>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchStaff} loading={loading} className="icon-btn" />
            </Tooltip>
            <Dropdown menu={csvMenuItems}>
              <Button icon={<FileExcelOutlined />} className="csv-btn">
                <span>CSV</span>
              </Button>
            </Dropdown>
            <Dropdown menu={pdfMenuItems}>
              <Button icon={<FilePdfOutlined />} className="pdf-btn">
                <span>PDF</span>
              </Button>
            </Dropdown>
            <Button type="primary" icon={<UserAddOutlined />} className="add-btn"
              onClick={() => setAddModalOpen(true)}>
              <span>Add Staff</span>
            </Button>

            {/* 📱 Mobile Stats Arrow Toggle */}
            {isMobile && (
              <button
                type="button"
                className="mobile-stats-icon-btn"
                onClick={() => setShowMobileStats(prev => !prev)}
              >
                {showMobileStats ? <UpOutlined /> : <DownOutlined />}
              </button>
            )}
          </div>
        </div>

                {/* Animated Smooth Stats Accordion */}
        <div className={`mini-stats-bar ${isMobile && showMobileStats ? 'show' : ''}`}>
          
          <div className="mini-stat total">
            <TeamOutlined /> 
            <div className="stat-content">
              <b>{stats.total}</b> <span>Total Staff</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat active">
            <CheckCircleOutlined /> 
            <div className="stat-content">
              <b>{stats.active}</b> <span>Active</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat inactive">
            <StopOutlined /> 
            <div className="stat-content">
              <b>{stats.inactive}</b> <span>Inactive</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat pending">
            <HourglassOutlined /> 
            <div className="stat-content">
              <b>{stats.pending}</b> <span>Pending</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat balance">
            <WalletOutlined /> 
            <div className="stat-content">
              <b>₹{stats.totalBalance.toLocaleString("en-IN")}</b>
              <span>Total Balance</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat transactions">
            <TransactionOutlined /> 
            <div className="stat-content">
              <b>{stats.transactions}</b><span>Transactions</span>
            </div>
          </div>

        </div>
      </div>

      {/* TABLE 1: ACTIVE STAFF */}
      <div className="table-card">
        <div className="table-section-header">
          <div className="section-title-wrap">
            <div className="section-icon-box active">
              <CheckCircleOutlined />
            </div>
            <div>
              <h3 className="section-title">Active Staff Members</h3>
              <p className="section-subtitle">
                {filteredStaff.length} active staff · Can login & access system
              </p>
            </div>
          </div>
          <Tag className="section-count-badge active">
            ✓ {staff.length} Active
          </Tag>
        </div>

        <div className="table-filters">
          <Input
            placeholder="Search active staff by name, profile ID, referral code..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="search-box"
            allowClear
          />
        </div>

        {/* 📱 Mobile Cards OR 💻 Desktop Table */}
        {isMobile ? (
          <div className="mobile-user-list">
            {filteredStaff.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No active staff found</div>
            ) : (
              filteredStaff.slice((activeCurrentPage - 1) * activePageSize, activeCurrentPage * activePageSize).map((r) => (
                <div className="mobile-user-card" key={r.id}>
                  <div className="muc-header">
                    <span className="muc-id">{getStaffProfileId(r)}</span>
                    <span className="muc-status active">● Active</span>
                  </div>
                  <div className="muc-body">
                    <div className="muc-avatar">
                      {r.first_name?.[0] || r.username?.[0] || "S"}
                    </div>
                    <div className="muc-info">
                      <h4 className="muc-name">{capitalize(r.first_name) || r.username || "N/A"}</h4>
                      <span className="muc-phone"><PhoneOutlined /> {r.contact || "N/A"}</span>
                    </div>
                  </div>
                  <div className="muc-footer">
                    <div className="muc-plan">Bal: ₹{getStaffBalance(r).toFixed(2)}</div>
                    <div className="muc-actions">
                      <button className="action-btn view" onClick={() => handleViewDetails(r)}><EyeOutlined /></button>
                      <button className="action-btn edit" onClick={() => handleEdit(r)}><EditOutlined /></button>
                      <button className="action-btn" style={{ background: "#fff7e6", color: "#faad14", border: "1px solid #ffd591" }} onClick={() => handleToggleStatus(r)}><StopOutlined /></button>
                      <button className="action-btn delete" onClick={() => handleDelete(r.id)}><DeleteOutlined /></button>
                    </div>
                  </div>
                </div>
              ))
            )}
            <Pagination
              current={activeCurrentPage}
              pageSize={activePageSize}
              total={filteredStaff.length}
              onChange={(page, size) => { setActiveCurrentPage(page); setActivePageSize(size); }}
              size="small"
              style={{ textAlign: 'center', marginTop: 16 }}
            />
          </div>
        ) : (
          <div className="desktop-table-wrapper">
            <Table
              rowKey="id"
              columns={activeColumns}
              dataSource={filteredStaff}
              loading={loading}
              size="small"
              pagination={{
                current: activeCurrentPage,
                pageSize: activePageSize,
                showSizeChanger: true,
                size: "small",
                showTotal: (total) => `Total ${total}`,
                onChange: (page, size) => {
                  setActiveCurrentPage(page);
                  setActivePageSize(size);
                },
              }}
              scroll={{ x: 950 }}
              locale={{ emptyText: "No active staff found" }}
            />
          </div>
        )}
      </div>

      {/* TABLE 2: INACTIVE STAFF */}
      {inactiveStaff.length > 0 && (
        <div className="table-card">
          <div className="table-section-header">
            <div className="section-title-wrap">
              <div className="section-icon-box inactive">
                <StopOutlined />
              </div>
              <div>
                <h3 className="section-title">Inactive Staff Members</h3>
                <p className="section-subtitle">
                  {inactiveStaff.length} deactivated staff · Cannot login
                </p>
              </div>
            </div>
            <Tag className="section-count-badge inactive">
              ⏸ {inactiveStaff.length} Inactive
            </Tag>
          </div>

          {isMobile ? (
            <div className="mobile-user-list">
              {inactiveStaff.slice((inactiveCurrentPage - 1) * inactivePageSize, inactiveCurrentPage * inactivePageSize).map((r) => (
                <div className="mobile-user-card" key={r.id}>
                  <div className="muc-header">
                    <span className="muc-id">{getStaffProfileId(r)}</span>
                    <span className="muc-status inactive">○ Inactive</span>
                  </div>
                  <div className="muc-body">
                    <div className="muc-avatar" style={{ background: 'linear-gradient(135deg, #faad14, #ffc53d)' }}>
                      {r.first_name?.[0] || r.username?.[0] || "S"}
                    </div>
                    <div className="muc-info">
                      <h4 className="muc-name">{capitalize(r.first_name) || r.username || "N/A"}</h4>
                      <span className="muc-phone" style={{ color: '#faad14' }}>{r.deactivation_reason || "Deactivated"}</span>
                    </div>
                  </div>
                  <div className="muc-footer">
                    <div className="muc-plan" style={{ color: '#888' }}>Deactivated</div>
                    <div className="muc-actions">
                      <button className="action-btn view" onClick={() => handleViewDetails(r)}><EyeOutlined /></button>
                      <button className="action-btn" style={{ background: "#dcfce7", color: "#16a34a", border: "1px solid #bbf7d0" }} onClick={() => handleToggleStatus(r)}><CheckCircleOutlined /></button>
                      <button className="action-btn delete" onClick={() => handleDelete(r.id)}><DeleteOutlined /></button>
                    </div>
                  </div>
                </div>
              ))}
              <Pagination
                current={inactiveCurrentPage}
                pageSize={inactivePageSize}
                total={inactiveStaff.length}
                onChange={(page, size) => { setInactiveCurrentPage(page); setInactivePageSize(size); }}
                size="small"
                style={{ textAlign: 'center', marginTop: 16 }}
              />
            </div>
          ) : (
            <div className="desktop-table-wrapper">
              <Table
                rowKey="id"
                columns={inactiveColumns}
                dataSource={inactiveStaff}
                size="small"
                pagination={{
                  current: inactiveCurrentPage,
                  pageSize: inactivePageSize,
                  showSizeChanger: true,
                  size: "small",
                  showTotal: (total) => `Total ${total}`,
                  onChange: (page, size) => {
                    setInactiveCurrentPage(page);
                    setInactivePageSize(size);
                  },
                }}
                scroll={{ x: 900 }}
              />
            </div>
          )}
        </div>
      )}

      {/* TABLE 3: PENDING STAFF */}
      {pendingStaff.length > 0 && (
        <div className="table-card">
          <div className="table-section-header">
            <div className="section-title-wrap">
              <div className="section-icon-box pending">
                <HourglassOutlined />
              </div>
              <div>
                <h3 className="section-title">Pending Staff Applications</h3>
                <p className="section-subtitle">
                  {pendingStaff.length} application(s) waiting for approval
                </p>
              </div>
            </div>
            <Tag className="section-count-badge pending">
              ⏳ {pendingStaff.length} Pending
            </Tag>
          </div>

          {isMobile ? (
            <div className="mobile-user-list">
              {pendingStaff.slice((pendingCurrentPage - 1) * pendingPageSize, pendingCurrentPage * pendingPageSize).map((r) => (
                <div className="mobile-user-card" key={r.id}>
                  <div className="muc-header">
                    <span className="muc-id" style={{ color: '#f59e0b', background: '#fff7ed' }}>{r.username}</span>
                    <span className="muc-status pending">⏳ Pending</span>
                  </div>
                  <div className="muc-body">
                    <div className="muc-avatar" style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}>
                      {r.first_name?.[0] || r.username?.[0] || "S"}
                    </div>
                    <div className="muc-info">
                      <h4 className="muc-name">{capitalize(r.first_name) || r.username || "N/A"}</h4>
                      <span className="muc-phone"><PhoneOutlined /> {r.contact || "N/A"}</span>
                    </div>
                  </div>
                  <div className="muc-footer">
                    <div className="muc-plan" style={{ color: '#888' }}>{formatDate(r.date_joined)}</div>
                    <div className="muc-actions">
                      <button className="action-btn view" onClick={() => handleViewDetails(r)}><EyeOutlined /></button>
                      <button className="action-btn" style={{ background: "#dcfce7", color: "#16a34a", border: "1px solid #bbf7d0" }} onClick={() => handleApproveStaff(r)}><CheckCircleOutlined /></button>
                      <button className="action-btn" style={{ background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca" }} onClick={() => handleRejectStaff(r)}><CloseCircleOutlined /></button>
                    </div>
                  </div>
                </div>
              ))}
              <Pagination
                current={pendingCurrentPage}
                pageSize={pendingPageSize}
                total={pendingStaff.length}
                onChange={(page, size) => { setPendingCurrentPage(page); setPendingPageSize(size); }}
                size="small"
                style={{ textAlign: 'center', marginTop: 16 }}
              />
            </div>
          ) : (
            <div className="desktop-table-wrapper">
              <Table
                rowKey="id"
                columns={pendingColumns}
                dataSource={pendingStaff}
                size="small"
                pagination={{
                  current: pendingCurrentPage,
                  pageSize: pendingPageSize,
                  showSizeChanger: true,
                  size: "small",
                  showTotal: (total) => `Total ${total}`,
                  onChange: (page, size) => {
                    setPendingCurrentPage(page);
                    setPendingPageSize(size);
                  },
                }}
                scroll={{ x: 900 }}
              />
            </div>
          )}
        </div>
      )}

      {/* ADD STAFF MODAL */}
      <Modal
        title={<div className="st-modal-title-new"><div className="st-modal-title-icon-wrap"><UserAddOutlined /></div>Add Staff</div>}
        open={addModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={720}
        destroyOnHidden
        forceRender
        className="st-add-modal"
        centered
      >
        <div className="st-staff-strip">
          <div className="st-staff-badge">
            <div className="st-staff-badge-icon admin"><IdcardOutlined /></div>
            <div>
              <div className="st-staff-badge-label">Admin</div>
              <div className="st-staff-badge-value">{adminName} (ADMIN)</div>
            </div>
          </div>
          <div className="st-staff-badge">
            <div className="st-staff-badge-icon role"><CrownOutlined /></div>
            <div>
              <div className="st-staff-badge-label">Creating</div>
              <div className="st-staff-badge-value">Staff Member</div>
            </div>
          </div>
        </div>

        <Form form={form} layout="vertical" onFinish={handleAddStaff} requiredMark={false}>
          <div className="st-form-body">
            <div className="st-form-section">
              <div className="st-form-grid-new">
                <Form.Item name="username" label={<span><UserOutlined style={{ marginRight: 6, color: "#667eea" }} />Username <span className="st-required-star">*</span></span>} rules={[{ required: true, message: "Username required" }]}>
                  <Input placeholder="e.g. staff2024" />
                </Form.Item>
                <Form.Item name="password" label={<span><LockOutlined style={{ marginRight: 6, color: "#667eea" }} />Password <span className="st-required-star">*</span></span>} rules={[{ required: true, message: "Password required" }]}>
                  <Input.Password placeholder="Strong password" autoComplete="new-password" />
                </Form.Item>
                <Form.Item name="contact" label={<span><PhoneOutlined style={{ marginRight: 6, color: "#667eea" }} />Mobile <span className="st-required-star">*</span></span>} rules={[{ required: true, message: "Mobile required" }, { pattern: /^[0-9]{10}$/, message: "Must be 10 digits" }]} normalize={(value) => value?.replace(/\D/g, "").slice(0, 10)}>
                  <Input placeholder="10-digit number" maxLength={10} inputMode="numeric" />
                </Form.Item>
                <Form.Item name="email" label={<span><MailOutlined style={{ marginRight: 6, color: "#667eea" }} />Email <span className="st-required-star">*</span></span>} rules={[{ required: true, message: "Email required" }, { type: "email", message: "Invalid email" }]}>
                  <Input placeholder="staff@email.com" />
                </Form.Item>
              </div>
            </div>

            <div className="st-form-section">
              <div className="st-section-header">
                <div className="st-section-emoji personal">👤</div>
                <div>
                  <div className="st-section-title">Personal Details</div>
                  <div className="st-section-subtitle">Staff basic information</div>
                </div>
              </div>
              <div className="st-form-grid-new">
                <Form.Item name="first_name" label="Full Name" rules={[{ required: true, message: "Name required" }]}>
                  <Input placeholder="Enter full name" />
                </Form.Item>
                <Form.Item name="gender" label="Gender" rules={[{ required: true, message: "Gender required" }]}>
                  <Select placeholder="Select gender" allowClear>
                    <Option value="Male">👨 Male</Option>
                    <Option value="Female">👩 Female</Option>
                    <Option value="Other">Other</Option>
                  </Select>
                </Form.Item>
              </div>
            </div>

            <div className="st-form-section">
              <div className="st-section-header">
                <div className="st-section-emoji location">📍</div>
                <div>
                  <div className="st-section-title">Location Details</div>
                  <div className="st-section-subtitle">Where does the staff live?</div>
                </div>
              </div>
              <div className="st-form-grid-new">
                <Form.Item name="state" label="State">
                  <Select placeholder="Select State" allowClear showSearch onChange={handleAddStateChange} filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                    {states.map((s) => (<Option key={s.state} value={s.state}>{s.state}</Option>))}
                  </Select>
                </Form.Item>
                <Form.Item name="district" label="District">
                  <Select placeholder={!form.getFieldValue('state') ? "Select State first" : addDistrictsLoading ? "Loading districts..." : addDistricts.length ? "Select District" : "No districts found"} allowClear showSearch loading={addDistrictsLoading} disabled={addDistrictsLoading || addDistricts.length === 0} filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                    {addDistricts.map((d) => (<Option key={d} value={d}>{d}</Option>))}
                  </Select>
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="st-form-footer">
            <div className="st-form-footer-left">
              <SafetyOutlined />
              <span>All data is securely stored</span>
            </div>
            <div className="st-form-footer-actions">
              <Button onClick={handleModalClose} className="st-cancel-btn">Cancel</Button>
              <Button type="primary" htmlType="submit" loading={addLoading} icon={<HeartFilled />} className="st-save-btn">Save Staff</Button>
            </div>
          </div>
        </Form>
      </Modal>

      {/* EDIT STAFF MODAL */}
      <Modal
        title={<div className="st-modal-title-new"><div className="st-modal-title-icon-wrap"><EditOutlined /></div>Edit Staff</div>}
        open={editModalOpen}
        onCancel={handleEditModalClose}
        footer={null}
        width={720}
        destroyOnHidden
        forceRender
        className="st-add-modal"
        centered
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateStaff} requiredMark={false}>
          <div className="st-form-body">
            <div className="st-form-section">
              <div className="st-form-grid-new">
                <Form.Item name="username" label={<span><UserOutlined style={{ marginRight: 6, color: "#667eea" }} />Username</span>}>
                  <Input placeholder="Username" disabled />
                </Form.Item>
                <Form.Item name="contact" label={<span><PhoneOutlined style={{ marginRight: 6, color: "#667eea" }} />Mobile</span>} rules={[{ pattern: /^[0-9]{10}$/, message: "Must be 10 digits" }]} normalize={(value) => value?.replace(/\D/g, "").slice(0, 10)}>
                  <Input placeholder="10-digit number" maxLength={10} inputMode="numeric" />
                </Form.Item>
                <Form.Item name="email" label={<span><MailOutlined style={{ marginRight: 6, color: "#667eea" }} />Email</span>} rules={[{ type: "email", message: "Invalid email" }]}>
                  <Input placeholder="staff@email.com" />
                </Form.Item>
                <Form.Item name="first_name" label="Full Name">
                  <Input placeholder="Enter full name" />
                </Form.Item>
                <Form.Item name="gender" label="Gender">
                  <Select placeholder="Select gender" allowClear>
                    <Option value="Male">👨 Male</Option>
                    <Option value="Female">👩 Female</Option>
                    <Option value="Other">Other</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="state" label="State">
                  <Select placeholder="Select State" allowClear showSearch onChange={handleEditStateChange}>
                    {states.map((s) => (<Option key={s.state} value={s.state}>{s.state}</Option>))}
                  </Select>
                </Form.Item>
                <Form.Item name="district" label="District">
                  <Select placeholder={editDistrictsLoading ? "Loading..." : editDistricts.length ? "Select District" : "Select State first"} allowClear showSearch loading={editDistrictsLoading} disabled={editDistrictsLoading || editDistricts.length === 0}>
                    {editDistricts.map((d) => (<Option key={d} value={d}>{d}</Option>))}
                  </Select>
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="st-form-footer">
            <div className="st-form-footer-left">
              <SafetyOutlined />
              <span>All changes are securely saved</span>
            </div>
            <div className="st-form-footer-actions">
              <Button onClick={handleEditModalClose} className="st-cancel-btn">Cancel</Button>
              <Button type="primary" htmlType="submit" loading={addLoading} icon={<EditOutlined />} className="st-save-btn">Update Staff</Button>
            </div>
          </div>
        </Form>
      </Modal>

      {/* DETAILS DRAWER */}
      <Drawer
        title={<div className="drawer-title"><IdcardOutlined /> Staff Profile</div>}
        placement="right"
        width={440}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
      >
        {selectedStaff && (
          <div className="user-details">
            <div className="profile-header">
              <Avatar size={72} className="profile-avatar staff">
                {selectedStaff.first_name?.[0] || selectedStaff.username?.[0] || "S"}
              </Avatar>
              <h2>{capitalize(selectedStaff.first_name) || selectedStaff.username}</h2>
              <p className="profile-ref">{getStaffProfileId(selectedStaff)}</p>
              <div className="profile-tags">
                <Tag className={`status-tag ${selectedStaff.is_active ? "active" : "inactive"}`}>
                  {selectedStaff.is_active ? "● Active" : "○ Inactive"}
                </Tag>
                <Tag className="role-tag">👤 Staff</Tag>
              </div>

              <div className="balance-showcase">
                <div className="balance-icon"><WalletOutlined /></div>
                <div className="balance-info">
                  <span className="balance-label">Current Balance</span>
                  <span className={`balance-value ${getStaffBalance(selectedStaff) < 0 ? "negative" : ""}`}>
                    {formatCurrency(getStaffBalance(selectedStaff))}
                  </span>
                </div>
              </div>

              <Button type="primary" icon={<PhoneOutlined />} className="call-btn-large"
                onClick={() => handleCall(selectedStaff.contact)}>
                Call {selectedStaff.contact || "N/A"}
              </Button>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div className="detail-section">
              <h4>Personal Details</h4>
              <DetailItem icon={UserOutlined} label="Name" value={capitalize(selectedStaff.first_name)} />
              <DetailItem icon={UserOutlined} label="Username" value={selectedStaff.username} />
              <DetailItem icon={UserOutlined} label="Gender" value={capitalize(selectedStaff.gender)} isLast />
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div className="detail-section">
              <h4>Contact Details</h4>
              <DetailItem icon={MailOutlined} label="Email" value={selectedStaff.email} />
              <DetailItem icon={PhoneOutlined} label="Mobile" value={selectedStaff.contact} />
              <DetailItem icon={EnvironmentOutlined} label="City" value={selectedStaff.district || selectedStaff.city} />
              <DetailItem icon={EnvironmentOutlined} label="State" value={selectedStaff.state} isLast />
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div className="detail-section">
              <h4>Account Information</h4>
              <DetailItem icon={CalendarOutlined} label="Joined" value={formatDate(selectedStaff.created_date || selectedStaff.date_joined)} />
              <DetailItem icon={IdcardOutlined} label="Profile ID" value={getStaffProfileId(selectedStaff)} isRef />
              <DetailItem icon={UserOutlined} label="Role" value="Staff Member" isLast />
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div className="detail-section">
              <h4><LinkOutlined /> Referral Information</h4>
              <DetailItem icon={IdcardOutlined} label="Code" value={getReferralCode(selectedStaff)} isRef isLast />

              <div className="referral-link-box">
                <div className="ref-link-label">REFERRAL LINK</div>
                <div className="ref-link-value">{generateReferralLink(selectedStaff)}</div>
                <Space size={6} style={{ marginTop: 8 }}>
                  <Button size="small" icon={<CopyOutlined />} className="ref-action-btn"
                    onClick={() => handleCopyLink(selectedStaff)}>Copy Link</Button>
                  <Button size="small" icon={<CopyOutlined />} className="ref-code-action-btn"
                    onClick={() => handleCopyCode(selectedStaff)}>Copy Code</Button>
                  <Button size="small" icon={<WhatsAppOutlined />} className="ref-whatsapp-btn"
                    onClick={() => handleShareWhatsApp(selectedStaff)}>Share</Button>
                </Space>
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div className="detail-section">
              <h4>
                <TransactionOutlined /> Transaction History
                <span className="section-count">
                  ({getStaffTransactions(selectedStaff.id).length})
                </span>
              </h4>
              <div className="transactions-list">
                {getStaffTransactions(selectedStaff.id).length === 0 ? (
                  <div className="empty-state">No transactions yet</div>
                ) : (
                  getStaffTransactions(selectedStaff.id).slice(0, 5).map((tx, idx) => (
                    <div key={idx} className="transaction-item">
                      <div className="tx-icon"><DollarOutlined /></div>
                      <div className="tx-info">
                        <div className="tx-title">{capitalize(tx.type) || "Transaction"}</div>
                        <div className="tx-date">{formatDate(tx.created_date || tx.date)}</div>
                      </div>
                      <div className={`tx-amount ${tx.type === "credit" ? "credit" : "debit"}`}>
                        {tx.type === "credit" ? "+" : "-"}₹{parseFloat(tx.amount || 0).toFixed(2)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div className="drawer-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button icon={<EditOutlined />} onClick={() => { setDetailsDrawerOpen(false); handleEdit(selectedStaff); }}>Edit</Button>
              <Button
                icon={selectedStaff.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
                style={{
                  background: selectedStaff.is_active ? "#fff7e6" : "#f6ffed",
                  color: selectedStaff.is_active ? "#faad14" : "#52c41a",
                  border: `1px solid ${selectedStaff.is_active ? "#ffd591" : "#b7eb8f"}`,
                  fontWeight: 600,
                }}
                onClick={() => { setDetailsDrawerOpen(false); handleToggleStatus(selectedStaff); }}
              >
                {selectedStaff.is_active ? "Deactivate" : "Activate"}
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={() => { setDetailsDrawerOpen(false); handleDelete(selectedStaff.id); }}>Delete</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default Staff_Tables;